import {
    DialogTitle,
    FormHelperText,
    FormLabel,
    Modal,
    ModalClose,
    ModalDialog,
    Option,
    Select,
    Slider,
    DialogContent,
    CircularProgress
} from "@mui/joy";
import React, {useEffect, useMemo, useState} from "react";
import Button from "@mui/joy/Button";
import DialogActions from '@mui/joy/DialogActions';
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {toast} from "react-toastify";
import GeneralApiService from "../../axios/Axios";
import {stripePromise} from "../../stripeConfig";

const formatGBP = (amount) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

function PaymentForm({amount, onSuccess}) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) return;
        setSubmitting(true);
        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.href
            },
            redirect: "if_required"
        });
        if (result.error) {
            toast.error(result.error.message || "Payment failed. Please try again.");
            setSubmitting(false);
            return;
        }
        if (result.paymentIntent && (result.paymentIntent.status === "succeeded" || result.paymentIntent.status === "processing")) {
            toast.success("Payment submitted successfully.");
            onSuccess && onSuccess();
        }
        setSubmitting(false);
    };

    return (
        <>
            <PaymentElement options={{layout: "tabs"}}/>
            <DialogActions>
                <Button
                    fullWidth
                    variant="solid"
                    color="success"
                    loading={submitting || !stripe || !elements}
                    onClick={handleSubmit}
                >
                    Pay {formatGBP(amount)} & turn on ads
                </Button>
            </DialogActions>
        </>
    );
}

export default function StripeLeadPaymentModal({openState, franchise, parentCallback, selectedAd, selectedAdToggled}) {
    const [open, setOpen] = useState(openState);
    const [toggleOffReason, setToggleOffReason] = useState<string>('stopping-operations');
    const [value, setValue] = useState<number>(100);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [loadingIntent, setLoadingIntent] = useState(false);
    const [confirmedAmount, setConfirmedAmount] = useState<number | null>(null);

    const needsTopUp = !!(franchise && franchise.lead_balance < 100);

    useEffect(() => {
        setOpen(openState);
    }, [openState]);

    useEffect(() => {
        if (franchise) {
            const minimumTopUp = Math.max(100 - franchise.lead_balance, 1);
            setValue(minimumTopUp);
        }
    }, [franchise]);

    // Reset payment state when modal closes
    useEffect(() => {
        if (!open) {
            setClientSecret(null);
            setConfirmedAmount(null);
        }
    }, [open]);

    const fetchPaymentIntent = async (amountToCharge: number) => {
        if (!franchise) return;
        setLoadingIntent(true);
        try {
            const response = await GeneralApiService.createLeadPaymentIntent(amountToCharge, true);
            setClientSecret(response.client_secret);
            setConfirmedAmount(amountToCharge);
        } catch (e) {
            toast.error("Could not start Stripe payment. Please try again.");
        } finally {
            setLoadingIntent(false);
        }
    };

    const handleContinueToPayment = () => {
        fetchPaymentIntent(value);
    };

    function closeModal() {
        setOpen(false);
        parentCallback('');
    }

    function toggleOffAds() {
        parentCallback(selectedAd, false, toggleOffReason);
    }

    function turnAdsOn() {
        parentCallback(selectedAd, true);
    }

    const handlePaymentSuccess = () => {
        turnAdsOn();
        closeModal();
    };

    const options = useMemo(() => clientSecret ? {
        clientSecret,
        appearance: {theme: 'stripe' as const}
    } : null, [clientSecret]);

    const showPaymentForm = !loadingIntent && options && confirmedAmount !== null;

    return (
        <>
            <Modal slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'none',
                        backgroundColor: 'rgb(108 108 108 / 60%)',
                        padding: '20px'
                    },
                },
            }} open={open} onClose={() => closeModal()}>
                <ModalDialog
                    sx={{
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        width: { xs: '95vw', sm: 'auto' },
                        maxWidth: { xs: '95vw', sm: '450px' },
                    }}
                >
                    <ModalClose></ModalClose>
                    {selectedAdToggled == 'Off' ? (
                        <>
                            <DialogTitle>Toggle {selectedAd.replace('_', ' ')} ads on</DialogTitle>
                            {needsTopUp ? (
                                <>
                                    <DialogContent sx={{maxWidth: '400px'}}>
                                        Your current balance is £{franchise.lead_balance}. You need at least
                                        £{100 - franchise.lead_balance} added to turn your ads on.
                                    </DialogContent>
                                    
                                    {!showPaymentForm && (
                                        <>
                                            <Slider
                                                value={value}
                                                onChange={(event: Event, newValue: number | number[]) => {
                                                    setValue(newValue as number);
                                                }}
                                                min={franchise ? Math.max(100 - franchise.lead_balance, 1) : 1}
                                                max={1250}
                                                step={1}
                                                valueLabelDisplay="on"
                                                valueLabelFormat={formatGBP}
                                                sx={{marginTop: 2}}
                                            />
                                            <DialogActions>
                                                <Button
                                                    fullWidth
                                                    color="primary"
                                                    loading={loadingIntent}
                                                    onClick={handleContinueToPayment}
                                                >
                                                    Continue to Payment ({formatGBP(value)})
                                                </Button>
                                            </DialogActions>
                                        </>
                                    )}

                                    {loadingIntent && (
                                        <DialogContent sx={{display: 'flex', justifyContent: 'center', my: 2}}>
                                            <CircularProgress/>
                                        </DialogContent>
                                    )}
                                    {showPaymentForm && (
                                        <Elements stripe={stripePromise} options={options}>
                                            <PaymentForm amount={confirmedAmount} onSuccess={handlePaymentSuccess}/>
                                        </Elements>
                                    )}
                                </>


                            ) : (
                                <>
                                    <DialogContent sx={{maxWidth: '400px'}}>
                                        Your current balance is £{franchise.lead_balance}.
                                    </DialogContent>
                                    <DialogActions>
                                        <Button variant="solid"
                                                color="success" onClick={() => {
                                            turnAdsOn();
                                            closeModal();
                                        }}>
                                            Turn On
                                        </Button>
                                        <Button variant="plain" color="neutral" onClick={() => closeModal()}>
                                            Cancel
                                        </Button>
                                    </DialogActions>
                                </>

                            )}
                        </>
                    ) : (
                        <>
                            <DialogTitle
                                sx={{padding: '10px'}}
                                color="danger"><WarningRoundedIcon/>Toggle {selectedAd.replace('_', ' ')} ads off
                            </DialogTitle>
                            <DialogContent sx={{marginTop: '5px', marginBottom: '5px'}}>
                                <FormLabel>Reason For Turning Off</FormLabel>
                                <Select onChange={(e, newValue) => {
                                    if (newValue) {
                                        setToggleOffReason(newValue);
                                    }
                                }} defaultValue="stopping-operations">
                                    <Option value="stopping-operations">Temporarily Stopping Operations</Option>
                                    <Option value="not-enough-leads">Not Enough Leads</Option>
                                    <Option value="too-many-leads">Too Many Leads At Once</Option>
                                </Select>
                                <FormHelperText sx={{color: 'red'}}>Regularly turning ads off and on can worsen
                                    performance of
                                    ads over time</FormHelperText>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="solid" color="danger" onClick={() => toggleOffAds()}>
                                    Turn Off
                                </Button>
                                <Button variant="plain" color="neutral" onClick={() => closeModal()}>
                                    Cancel
                                </Button>
                            </DialogActions>
                        </>

                    )}

                </ModalDialog>
            </Modal>
        </>
    );

}